import { ApolloProvider } from '@apollo/client'
import { config } from '_appConfig'
import { gqlClient } from 'gql'
import { createBrowserHistory } from 'history'
import moment from 'moment'
import 'moment/locale/ru'
import 'overlayscrollbars/css/OverlayScrollbars.css'
import { useEffect, useState } from 'react'
import { DndProvider } from 'react-dnd'
import { HTML5Backend } from 'react-dnd-html5-backend'
import { createRoot } from 'react-dom/client'
import { HelmetProvider } from 'react-helmet-async'
import { I18nextProvider } from 'react-i18next'
import { Provider } from 'react-redux'
import { Redirect, Route, Router, Switch } from 'react-router-dom'

import { NotFoundPortalGate } from 'components/contentspace/portal/NotFoundPortal'
import {
  appendManualLoginParam,
  markAutoLoginBypassedAfterLogout,
} from 'components/contentspace/portal/autoLogin'
import { clearPortalSession } from 'components/contentspace/sessionCookie'
import Loader from 'components/uiKit/Loader'
import { PORTAL_V2_COURSE_MATCH, PORTAL_V2_LMS_COURSE_MATCH } from 'constants/paths'
import BrandProvider from 'services/Branding/BrandProvider'
import { mergeBrands } from 'services/Branding/mergeBrands'
import type { BrandType } from 'services/Branding/types'
import store from 'services/Store/store'
import i18n from 'services/Translation/i18n'
import { validateFontFamilyCss } from 'utils/font'

import './fonts/inter.config'
import { ContentSpaceAuthGate } from './routes/portal/ContentSpacePublic/components/ContentSpaceAuthGate'
import {
  usePortalParams,
  usePortalBasePath,
} from './routes/portal/ContentSpacePublic/hooks/usePortalParams'
import { PortalLanding } from './routes/portal/ContentSpacePublic/pages/PortalLanding/PortalLanding'
import { PortalLms } from './routes/portal/ContentSpacePublic/pages/PortalLms/PortalLms'
import PortalV2Player from './routes/portal/PortalV2Player'
import './theme.scss'
import './theme/deprecated_reset.css'
import { initTheme } from './theme/initTheme'
import './theme/reset.css'
import './theme/theme.scss'

// Portal V2 entry — inline player SPA (now default, served at portal root).
// Lighter than src/app.tsx: no Uppy, no blockPatch middleware, no editor shell, no admin routes.
// Marks the global `__PORTAL_V2__` flag synchronously on module load so shared helpers
// (e.g. buildCoursePath in usePortalParams) can detect V2 bundle vs V1 LMS bundle.

// @ts-expect-error window global injected by backend
window.__PORTAL_V2__ = true

moment.locale(i18n.language)
initTheme()

// @ts-expect-error window global injected by backend
const portalV2Config = window.__PORTAL_CONFIG__ as
  | { companyId: string; portalId: string; brand?: { theme: unknown; font?: unknown } }
  | undefined

const portalBrand = portalV2Config?.brand
  ? mergeBrands(portalV2Config.brand as BrandType)
  : undefined

const portalFontFamily = portalBrand?.font
  ? validateFontFamilyCss(portalBrand.font.desktop.base.fontFamily.value)
  : undefined

// Derive basename from URL — V2 now owns portal root, so basename is the portal
// mount point (main domain) or the site root (custom domain).
const getPortalV2Basename = (): string => {
  const path = window.location.pathname
  // Main parta domain: /contentspace/public/portal/<id>/...
  const mainMatch = path.match(/^(\/contentspace\/public\/portal\/[^/]+)/)
  if (mainMatch) {
    return mainMatch[1]
  }
  // Custom domain: "/" — basename is the site root.
  return ''
}

const portalV2History = createBrowserHistory({ basename: getPortalV2Basename() })

// Logs the user out: clears cookie, calls backend logout, then redirects to
// landing with ?login=manual so a portal that has SSO autoLogin enabled does
// not bounce the user straight back into the IdP.
const PortalLogout: React.FC = () => {
  const { portalId } = usePortalParams()
  const basePath = usePortalBasePath()
  const [done, setDone] = useState(false)

  useEffect(() => {
    if (portalId) {
      clearPortalSession(portalId)
      markAutoLoginBypassedAfterLogout(portalId)
    }

    fetch(`${config.io.contentspaceEndpoint}/public/portal/${portalId}/logout`, {
      method: 'POST',
      credentials: 'include',
    })
      .catch(() => {})
      .finally(() => setDone(true))
  }, [portalId])

  if (!done) {
    return <Loader name='portalV2Logout' />
  }

  return <Redirect to={appendManualLoginParam(basePath || '/')} />
}

const PortalNotFound: React.FC = () => {
  return <NotFoundPortalGate />
}

const PortalRoutes: React.FC = () => {
  const routes = (
    <Switch>
      <Route component={PortalLogout} path='/logout' exact />
      <ContentSpaceAuthGate>
        <Switch>
          {/* Course viewer — only canonical shapes match, non-canonical URLs fall to 404 */}
          <Route component={PortalV2Player} path={[...PORTAL_V2_COURSE_MATCH]} exact />
          <Route component={PortalLms} path={[...PORTAL_V2_LMS_COURSE_MATCH]} exact />
          {/* Portal landing (list of courses) */}
          <Route component={PortalLanding} path='/' exact />
          {/* 404 fallback */}
          <Route render={() => <PortalNotFound />} />
        </Switch>
      </ContentSpaceAuthGate>
    </Switch>
  )

  // Course uses overlayscrollbars which requires a fixed-height ancestor to calculate
  // its viewport. Without `height: 100vh` the whole course collapses to header height.
  const wrapperStyle: React.CSSProperties = {
    fontFamily: portalFontFamily,
    height: '100vh',
    display: 'flex',
    flexDirection: 'column',
  }

  if (portalBrand) {
    return (
      <BrandProvider brand={portalBrand}>
        <div style={wrapperStyle}>{routes}</div>
      </BrandProvider>
    )
  }

  return <div style={wrapperStyle}>{routes}</div>
}

const PortalApp: React.FC = () => {
  return (
    <Provider store={store}>
      <HelmetProvider>
        <I18nextProvider i18n={i18n}>
          <ApolloProvider client={gqlClient.core}>
            <DndProvider backend={HTML5Backend}>
              <Router history={portalV2History}>
                <PortalRoutes />
              </Router>
            </DndProvider>
          </ApolloProvider>
        </I18nextProvider>
      </HelmetProvider>
    </Provider>
  )
}

const container = document.getElementById('root')

if (container) {
  const root = createRoot(container)
  root.render(<PortalApp />)
}
